import {
  AppStreamer,
  DirectConfig,
  eAction,
  eStatus,
  LogFormat,
  LogLevel,
  StreamEvent,
  StreamType,
} from '@nvidia/omniverse-webrtc-streaming-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PEP_API_PATH_V3 } from 'src/services';
import { StreamingApp } from '../state/Apps';

export interface UseStreamOptions {
  app: StreamingApp;
  sessionId: string;
  videoElementId?: string;
  audioElementId?: string;
}

export interface UseStreamResult {
  loading: boolean;
  error: Error | string;
  terminate: () => Promise<void>;
}

/**
 * Hook to establish WebRTC streaming connection to an existing session
 * Based on ov-dgxc-portal-sample implementation
 */
export default function useStream({
  app,
  sessionId,
  videoElementId = 'stream-video',
  audioElementId = 'stream-audio',
}: UseStreamOptions): UseStreamResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | string>('');

  const initialized = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      console.log('⚠️ No sessionId provided');
      return undefined;
    }

    if (!app) {
      console.log('⚠️ No app provided');
      return undefined;
    }

    if (initialized.current) {
      console.log('⚠️ Already initialized');
      return undefined;
    }

    initialized.current = true;

    console.log('=== useStream: Initializing ===');
    console.log('App:', app);
    console.log('Session ID:', sessionId);

    setLoading(true);
    setError('');

    // Event handlers for WebRTC stream
    function onUpdate(message: StreamEvent) {
      console.log('📡 onUpdate', message);
    }

    function onStart(message: StreamEvent) {
      console.log('🚀 onStart', message);

      if (message.action === eAction.start) {
        if (message.status === eStatus.success) {
          console.log('✅ Stream started successfully');

          const video = document.getElementById(
            videoElementId
          ) as HTMLVideoElement;

          if (video) {
            video.play().catch((error) => {
              console.error('❌ Failed to play video:', error);
              setError(error as Error);
            });
          }

          setLoading(false);
        } else if (message.status === eStatus.error) {
          console.error('❌ Stream start error:', message.info);
          setError(message.info || 'Failed to start stream');
          setLoading(false);
        } else if (message.status === eStatus.warning) {
          console.warn('⚠️ Stream start warning:', message.info);
        }
      }
    }

    function onStop(message: StreamEvent) {
      console.log('⏸️ onStop', message);
    }

    function onTerminate(message: StreamEvent) {
      console.log('🛑 onTerminate', message);
    }

    function onStreamStats(message: StreamEvent) {
      // Uncomment for detailed stats logging
      // console.log("📊 onStreamStats", message);
    }

    function onCustomEvent(message: unknown) {
      console.log('🎯 onCustomEvent', message);
    }

    const params = createStreamConfig(app, sessionId);

    async function connect() {
      try {
        console.log('🔌 Attempting to connect to stream...');
        console.log('Stream params:', params);

        // Check if session exists (optional - comment out if endpoint not available)
        const sessionExists = await checkSession(sessionId);
        if (!sessionExists) {
          console.warn('⚠️ Session check failed, but continuing anyway...');
          // Don't fail - continue with connection attempt
        }

        console.log('📞 Calling AppStreamer.connect...');

        await AppStreamer.connect({
          streamSource: StreamType.NVCF,
          logLevel: LogLevel.DEBUG,
          logFormat: LogFormat.TEXT,
          streamConfig: {
            videoElementId,
            audioElementId,
            maxReconnects: 3,
            nativeTouchEvents: true,
            ...params,
            onUpdate,
            onStart,
            onStop,
            onTerminate,
            onStreamStats,
            onCustomEvent,
          },
        });

        console.log('✅ AppStreamer.connect successful');
      } catch (error) {
        console.error('❌ Connection error:', error);
        setError(
          'info' in (error as StreamEvent)
            ? (error as StreamEvent).info
            : (error as Error)
        );
        setLoading(false);
      }
    }

    async function start() {
      console.log('🎬 Starting stream connection...');
      await connect();
    }

    start().catch((err) => {
      console.error('Failed to start stream:', err);
    });

    // Cleanup function
    return () => {
      console.log('🧹 useStream cleanup - terminating stream');
      // Only terminate in production to avoid dev issues with hot reload
      if (process.env.NODE_ENV === 'production') {
        AppStreamer.terminate().catch((err) => {
          console.error('Failed to terminate stream:', err);
        });
      }
    };
  }, [app, sessionId, videoElementId, audioElementId]);

  const terminate = useCallback(async () => {
    console.log('🛑 Terminating stream...');
    try {
      await AppStreamer.terminate(true);
      console.log('✅ Stream terminated');
    } catch (error) {
      console.error('❌ Error terminating stream:', error);
      setError(
        'info' in (error as StreamEvent)
          ? (error as StreamEvent).info
          : (error as Error)
      );
    }
  }, []);

  return {
    loading,
    error,
    terminate,
  };
}

/**
 * Check if session exists and is valid
 * Returns true if session is active, false otherwise
 */
async function checkSession(sessionId: string): Promise<boolean> {
  // Use proxy path for backend API
  const backend = PEP_API_PATH_V3;

  // Simple endpoint format: /sessions/{sessionId}/sign_in
  const url = `${backend}/sessions/${sessionId}/sign_in`;

  console.log('🔍 Checking session:', url);

  try {
    // Get token from localStorage (stored by Okta)
    const token = localStorage.getItem('okta-token-storage');
    const accessToken = token
      ? JSON.parse(token)?.accessToken?.accessToken
      : null;

    const response = await fetch(url, {
      method: 'HEAD',
      credentials: 'include',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });

    console.log('✅ Session check response:', response.ok, response.status);
    return response.ok;
  } catch (error) {
    console.error(`❌ Failed to check session:`, error);
    return false;
  }
}

/**
 * Creates WebRTC configuration parameters for streaming
 * Returns DirectConfig with signaling server details
 */
function createStreamConfig(
  app: StreamingApp,
  sessionId: string
): Partial<DirectConfig> {
  const params: DirectConfig = {
    width: 1920,
    height: 1080,
    fps: 60,
    mic: false,
    cursor: 'free',
    autoLaunch: true,

    // Specifies that the default streaming endpoint must not be used.
    // Enables signaling parameters for the component.
    server: '',
  };

  // If app has custom media server, use it
  if (app.mediaServer) {
    console.log('🎥 Using custom media server:', app.mediaServer);
    params.mediaServer = app.mediaServer;
    if (app.mediaPort) {
      params.mediaPort = app.mediaPort;
    }
  }

  // Create signaling URL
  const signalingURL = createStreamURL(sessionId);

  console.log('📡 Signaling URL:', signalingURL.toString());

  params.signalingServer = signalingURL.hostname;

  // Determine signaling port
  if (signalingURL.port) {
    params.signalingPort = Number(signalingURL.port);
  } else {
    params.signalingPort = signalingURL.protocol === 'https:' ? 443 : 80;
  }

  params.signalingPath = signalingURL.pathname;
  params.signalingQuery = new URLSearchParams();

  console.log('⚙️ Stream config:', {
    server: params.signalingServer,
    port: params.signalingPort,
    path: params.signalingPath,
    mediaServer: params.mediaServer,
  });

  return params;
}

/**
 * Constructs signaling URL for WebRTC connection
 * Simple format matching sample project: /sessions/{sessionId}
 * WebRTC library will append /sign_in automatically
 * HttpOnly cookies are automatically sent by the browser
 */
function createStreamURL(sessionId: string): URL {
  // Use proxy path for backend API
  let backend = PEP_API_PATH_V3;

  // For relative URLs, construct full URL with current origin
  if (backend.startsWith('/')) {
    backend = `${window.location.origin}${backend}`;
  }

  if (!backend.endsWith('/')) {
    backend += '/';
  }

  // Simple format: {backend}/sessions/{sessionId}
  const streamURL = new URL(`sessions/${sessionId}`, backend);

  console.log('🔗 Constructed stream URL:', streamURL.toString());
  console.log('🍪 HttpOnly cookies will be sent automatically by browser');

  return streamURL;
}
